import { useState } from 'react';
import { trackError } from '#api/errors.ts';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import { IdentityProviderDetails } from '../../detail/identity-provider-details/IdentityProviderDetails.tsx';
import { useDeleteEndpointIdentityProviderMutation } from './DeleteEndpointIdentityProviderDialog.queries.ts';

type Props = {
  endpointOIDC: EndpointOIDC;
  onClose: () => void;
};

export function DeleteEndpointIdentityProviderDialog({ endpointOIDC, onClose }: Props) {
  const [globalErrorMsg, setGlobalErrorMsg] = useState<string | null>(null);
  const deleteOIDCProvider = useDeleteEndpointIdentityProviderMutation();

  // Clears out the validation error message, in case the modal is re-opened.
  function closeModal() {
    setGlobalErrorMsg(null);
    onClose();
  }

  return (
    <ConfirmDialog
      title={<div className="truncate">Delete endpoint SSO provider?</div>}
      disableAccept={deleteOIDCProvider.isPending}
      acceptButtonText="Delete"
      onCancel={closeModal}
      onAccept={async () => {
        setGlobalErrorMsg(null);
        try {
          await deleteOIDCProvider.mutateAsync(endpointOIDC.id);
          closeModal();
        } catch (error: unknown) {
          trackError(error);
          setGlobalErrorMsg('We couldn’t delete your identity provider, please try again.');
        }
      }}
    >
      <p className="p mt-0">
        Deleting your endpoint SSO provider will immediately remove the ability for members of your organization to
        login to DNClient Desktop using SSO.
      </p>
      <ul className="ul mb-24">
        <li className="p">Existing sessions will keep working until their certificates expire</li>
        <li className="p">You can block or delete devices anytime from this panel</li>
      </ul>
      <div className="mb-24">
        <IdentityProviderDetails idp={endpointOIDC} />
      </div>
      <p className="p">Are you sure you want to delete this endpoint SSO provider?</p>
      {globalErrorMsg && <ErrorMessage className="mt-32">{globalErrorMsg}</ErrorMessage>}
    </ConfirmDialog>
  );
}
